import React, { useState } from "react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ForumIcon from "@mui/icons-material/Forum";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FlagIcon from "@mui/icons-material/Flag";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import WbTwilightIcon from "@mui/icons-material/WbTwilight";
import PersonIcon from "@mui/icons-material/Person";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import programa_preliminar from "./programa_preliminar_data";

const primary = "#0d1e4a";
const gold = "#c9a43b";
const goldSoft = "rgba(201,164,59,0.12)";

const TIPOS = {
    inauguracion: { badge: "Inauguración", Icon: FlagIcon },
    conversatorio: { badge: "Conversatorio", Icon: ForumIcon },
    panel: { badge: "Panel", Icon: GroupsIcon },
    cierre: { badge: "Sesión de Cierre", Icon: EmojiEventsIcon },
    conferencia: { badge: null, Icon: RecordVoiceOverIcon },
    registro: { Icon: HowToRegIcon, receso: true },
    cafe: { Icon: LocalCafeIcon, receso: true },
    almuerzo: { Icon: RestaurantMenuIcon, receso: true },
    fin: { Icon: WbTwilightIcon, receso: true },
};

const Ponente = ({ texto }) => {
    const esModeracion = /^Moderador(a)?:/i.test(texto);
    const contenido = esModeracion ? texto.replace(/^Moderador(a)?:\s*/i, "") : texto;
    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <PersonIcon sx={{ fontSize: 16, color: gold, mt: "3px", flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: "rgba(13,30,74,0.75)", lineHeight: 1.5 }}>
                {esModeracion && (
                    <Box component="span" sx={{ color: gold, fontWeight: 700, mr: 0.5 }}>
                        Modera:
                    </Box>
                )}
                {contenido}
            </Typography>
        </Stack>
    );
};

const Receso = ({ item, Icon }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.25,
            borderRadius: 99,
            border: `1.5px dashed rgba(201,164,59,0.55)`,
            background: "rgba(201,164,59,0.06)",
            width: "fit-content",
            maxWidth: "100%",
            mx: { xs: 0, md: "auto" },
        }}
    >
        <Icon sx={{ fontSize: 18, color: gold }} />
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: primary, letterSpacing: 0.5, textTransform: "uppercase" }}>
            {item.hora} · {item.titulo}
        </Typography>
    </Box>
);

const Actividad = ({ item, tipo }) => {
    const { badge, Icon } = tipo;
    return (
        <Box
            sx={{
                position: "relative",
                background: "#fff",
                borderRadius: 3,
                border: "1px solid rgba(13,30,74,0.08)",
                borderLeft: `4px solid ${badge ? gold : "rgba(13,30,74,0.15)"}`,
                boxShadow: "0 2px 12px rgba(13,30,74,0.06)",
                p: { xs: 2, md: 3 },
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                "&:hover": {
                    transform: "translateX(6px)",
                    boxShadow: "0 8px 28px rgba(13,30,74,0.14)",
                    borderLeftColor: gold,
                },
            }}
        >
            <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 99,
                            background: primary,
                        }}
                    >
                        <AccessTimeIcon sx={{ fontSize: 14, color: gold }} />
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff", letterSpacing: 1 }}>
                            {item.hora}
                        </Typography>
                    </Box>
                    {badge && (
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.65,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 99,
                                background: `linear-gradient(90deg, ${gold}, #b08d2a)`,
                            }}
                        >
                            <Icon sx={{ fontSize: 14, color: "#fff" }} />
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 800,
                                    color: "#fff",
                                    letterSpacing: 1.5,
                                    textTransform: "uppercase",
                                }}
                            >
                                {badge}
                            </Typography>
                        </Box>
                    )}
                </Stack>

                <Typography
                    sx={{
                        color: primary,
                        fontWeight: 700,
                        fontSize: { xs: "1rem", md: "1.15rem" },
                        lineHeight: 1.35,
                    }}
                >
                    {item.titulo}
                </Typography>

                {item.ponentes && item.ponentes.length > 0 && (
                    <Stack spacing={0.75} sx={{ pt: 0.5, borderTop: "1px dashed rgba(13,30,74,0.1)" }}>
                        {item.ponentes.map((ponente, i) => (
                            <Ponente key={i} texto={ponente} />
                        ))}
                    </Stack>
                )}
            </Stack>
        </Box>
    );
};

export default function ProgramaPreliminar() {
    const [diaActivo, setDiaActivo] = useState(0);
    const dia = programa_preliminar.dias[diaActivo];

    return (
        <Box sx={{ width: "100%", background: "#f7f8fb", pb: { xs: 6, md: 10 } }}>
            {/* HERO */}
            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    background: `linear-gradient(135deg, ${primary} 0%, #14275c 55%, #1b3273 100%)`,
                    px: { xs: "6%", md: "10%" },
                    py: { xs: 6, md: 8 },
                    textAlign: "center",
                }}
            >
                {/* Glow decorativo */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "-40%",
                        right: "-10%",
                        width: 420,
                        height: 420,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(201,164,59,0.22) 0%, transparent 70%)",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-50%",
                        left: "-8%",
                        width: 380,
                        height: 380,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(201,164,59,0.14) 0%, transparent 70%)",
                        pointerEvents: "none",
                    }}
                />

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <Stack spacing={2.5} alignItems="center">
                        <Chip
                            label={programa_preliminar.titulo.toUpperCase()}
                            sx={{
                                background: goldSoft,
                                color: gold,
                                fontWeight: 800,
                                letterSpacing: 2,
                                border: `1.5px solid rgba(201,164,59,0.45)`,
                                fontSize: "0.7rem",
                            }}
                        />
                        <Typography
                            sx={{
                                color: "#fff",
                                fontWeight: 800,
                                fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.9rem" },
                                lineHeight: 1.3,
                                maxWidth: 900,
                            }}
                        >
                            {programa_preliminar.evento}
                        </Typography>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={{ xs: 1, sm: 3 }}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <CalendarMonthIcon sx={{ fontSize: 18, color: gold }} />
                                <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem", fontWeight: 600 }}>
                                    {programa_preliminar.fecha}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <LocationOnIcon sx={{ fontSize: 18, color: gold }} />
                                <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem", fontWeight: 600 }}>
                                    {programa_preliminar.sede}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem", letterSpacing: 1 }}>
                            {programa_preliminar.organizadores}
                        </Typography>
                    </Stack>
                </motion.div>
            </Box>

            {/* SELECTOR DE DÍA */}
            <Stack alignItems="center" sx={{ mt: { xs: -3, md: -3.5 }, position: "relative", zIndex: 2, px: "5%" }}>
                <Box
                    sx={{
                        display: "flex",
                        background: "#fff",
                        borderRadius: 99,
                        p: 0.6,
                        boxShadow: "0 8px 30px rgba(13,30,74,0.18)",
                        border: "1px solid rgba(13,30,74,0.06)",
                        maxWidth: "100%",
                    }}
                >
                    {programa_preliminar.dias.map((d, i) => (
                        <Box
                            key={i}
                            onClick={() => setDiaActivo(i)}
                            sx={{
                                px: { xs: 2, sm: 3.5 },
                                py: 1.25,
                                borderRadius: 99,
                                cursor: "pointer",
                                textAlign: "center",
                                transition: "all 0.3s ease",
                                background: diaActivo === i ? primary : "transparent",
                                border: diaActivo === i ? `2px solid ${gold}` : "2px solid transparent",
                                "&:hover": {
                                    background: diaActivo === i ? primary : goldSoft,
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                                    color: diaActivo === i ? gold : primary,
                                    lineHeight: 1.2,
                                }}
                            >
                                {d.etiqueta}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.65rem", sm: "0.72rem" },
                                    fontWeight: 600,
                                    color: diaActivo === i ? "rgba(255,255,255,0.8)" : "rgba(13,30,74,0.55)",
                                }}
                            >
                                {d.fecha}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Stack>

            {/* TIMELINE */}
            <Box sx={{ px: { xs: "5%", md: "12%", lg: "18%" }, mt: { xs: 4, md: 6 } }}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={diaActivo}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.35 }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                pl: { xs: 3, md: 4 },
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: { xs: 5, md: 9 },
                                    top: 8,
                                    bottom: 8,
                                    width: "2px",
                                    background: `linear-gradient(180deg, ${gold}, rgba(201,164,59,0.15))`,
                                },
                            }}
                        >
                            <Stack spacing={2.5}>
                                {dia.agenda.map((item, i) => {
                                    const tipo = TIPOS[item.tipo] || TIPOS.conferencia;
                                    return (
                                        <motion.div
                                            key={`${diaActivo}-${i}`}
                                            initial={{ opacity: 0, x: -16 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.35, delay: Math.min(i * 0.05, 0.6) }}
                                            style={{ position: "relative" }}
                                        >
                                            {/* Punto del timeline */}
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    left: { xs: -25, md: -25 },
                                                    top: tipo.receso ? 12 : 22,
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: "50%",
                                                    background: tipo.receso ? "#f7f8fb" : gold,
                                                    border: `2.5px solid ${gold}`,
                                                    boxShadow: tipo.receso ? "none" : "0 0 0 4px rgba(201,164,59,0.18)",
                                                }}
                                            />
                                            {tipo.receso ? (
                                                <Receso item={item} Icon={tipo.Icon} />
                                            ) : (
                                                <Actividad item={item} tipo={tipo} />
                                            )}
                                        </motion.div>
                                    );
                                })}
                            </Stack>
                        </Box>
                    </motion.div>
                </AnimatePresence>

                <Typography
                    sx={{
                        mt: 5,
                        textAlign: "center",
                        fontSize: "0.78rem",
                        color: "rgba(13,30,74,0.5)",
                        fontStyle: "italic",
                    }}
                >
                    * Programa académico preliminar, sujeto a cambios y confirmaciones.
                </Typography>
            </Box>
        </Box>
    );
}
